/**
 * Semicircular risk gauge (pure SVG, no deps).
 * 0–39 green, 40–69 amber, 70–100 red.
 */
import React from "react";

export default function RiskGauge({ score = 0, band, color, size = 280 }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const W = size, H = size * 0.6;
  const cx = W / 2, cy = H * 0.92, r = W * 0.42;
  const startAngle = Math.PI;
  const endAngle = 0;
  const valueAngle = startAngle + (endAngle - startAngle) * (s / 100);

  const polar = (a) => [cx + r * Math.cos(a), cy + r * Math.sin(a)];

  // Three arc segments (green / amber / red)
  const arc = (a0, a1, stroke) => {
    const [x0, y0] = polar(a0);
    const [x1, y1] = polar(a1);
    const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
    return (
      <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`}
            stroke={stroke} strokeWidth={size * 0.06} fill="none" strokeLinecap="butt" />
    );
  };
  const a39 = startAngle + (endAngle - startAngle) * 0.39;
  const a69 = startAngle + (endAngle - startAngle) * 0.69;

  // Needle
  const [nx, ny] = polar(valueAngle);
  const finalColor = color ||
    (s >= 70 ? "#ef4444" : s >= 40 ? "#f59e0b" : "#10b981");

  return (
    <svg data-testid="risk-gauge" width={W} height={H + 30} viewBox={`0 0 ${W} ${H + 30}`}>
      {/* Background track */}
      <path d={`M ${polar(startAngle)[0]} ${polar(startAngle)[1]}
                A ${r} ${r} 0 1 1 ${polar(endAngle)[0]} ${polar(endAngle)[1]}`}
            stroke="#1e2530" strokeWidth={size * 0.06} fill="none" />
      {arc(startAngle, a39, "#10b981")}
      {arc(a39, a69, "#f59e0b")}
      {arc(a69, endAngle, "#ef4444")}
      {/* Needle */}
      <line x1={cx} y1={cy} x2={nx} y2={ny}
            stroke={finalColor} strokeWidth={size * 0.012}
            strokeLinecap="round" />
      <circle cx={cx} cy={cy} r={size * 0.022} fill={finalColor} />
      {/* Score text */}
      <text x={cx} y={cy - size * 0.08} textAnchor="middle"
            fill="#f1f5f9" fontSize={size * 0.20} fontWeight="800"
            style={{ fontFamily: "ui-monospace, monospace" }}>
        {s}
      </text>
      <text x={cx} y={cy - size * 0.005} textAnchor="middle"
            fill={finalColor} fontSize={size * 0.045} fontWeight="700"
            letterSpacing={1.5} style={{ textTransform: "uppercase" }}>
        {band || (s >= 70 ? "HIGH" : s >= 40 ? "MEDIUM" : "LOW")}
      </text>
      {/* Scale labels */}
      <text x={polar(startAngle)[0]} y={polar(startAngle)[1] + 18} fill="#64748b" fontSize="11" textAnchor="middle">0</text>
      <text x={polar(endAngle)[0]}   y={polar(endAngle)[1] + 18}   fill="#64748b" fontSize="11" textAnchor="middle">100</text>
    </svg>
  );
}
